/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import { AnalyticsResultFormatterMessage } from "../../../messages/analytics-result-formatter.message";

const mapNameToCountryCode = (name: string) => {
  const lower = name?.trim()?.toLowerCase() || '';
  if (lower.includes('dominica')) return 'dm';
  if (lower.includes('grenada')) return 'gd';
  return null;
};

const alignSparseData = (
  globalLabels: string[],
  countryLabels: string[],
  sparseData: number[]
): number[] => {
  const aligned = Array(globalLabels.length).fill(0);
  globalLabels.forEach((gLabel, idx) => {
    const cIdx = countryLabels.indexOf(gLabel);
    if (cIdx !== -1 && sparseData[cIdx] !== undefined) {
      aligned[idx] = Math.round(sparseData[cIdx]);
    }
  });
  return aligned;
};

export const triggerSalesValue = (lifeCycle: any) => {
  const message = lifeCycle?.fsm?.message as AnalyticsResultFormatterMessage;
  const template = message?.template?.layout[0]?.colProps;
  const found = template?.find((o: any) => o.key === "salesValue");
  if (found?.compProps) {
    found.compProps.value = message?.raw?.salesValue?.total?.formatted;
  }
  if (found?.compProps) {
    if (message?.raw?.salesValue?.day?.labels) {
      found.compProps.salesValue.day.labels = message?.raw?.salesValue?.day?.labels;
    }
    if (message?.raw?.salesValue?.week?.labels) {
      found.compProps.salesValue.week.labels = message?.raw?.salesValue?.week?.labels;
    }
    if (message?.raw?.salesValue?.month?.labels) {
      found.compProps.salesValue.month.labels = message?.raw?.salesValue?.month?.labels;
    }
    if (message?.raw?.salesValue?.year?.labels) {
      found.compProps.salesValue.year.labels = message?.raw?.salesValue?.year?.labels;
    }

    const periods = ['day', 'week', 'month', 'year'] as const;

    for (const period of periods) {
      const rawPeriod = (message?.raw?.salesValue as any)?.[period];
      const compPeriod = (found.compProps.salesValue as any)?.[period];

      if (rawPeriod?.chartSeries) {
        const globalLabels = rawPeriod.labels || [];

        for (const obj of compPeriod?.chartSeries || []) {
          const countryCode = mapNameToCountryCode(obj.name);
          const _found = rawPeriod.chartSeries.find((o: any) => {
            const rawName = o.name?.trim()?.toLowerCase();
            const configName = obj?.name?.trim()?.toLowerCase();
            if (rawName === configName) return true;
            if (countryCode) {
              const rawCountryCode = mapNameToCountryCode(o.name);
              return rawCountryCode === countryCode;
            }
            return false;
          });
          
          console.log(`[period: ${period}] Processing ${obj.name} (country: ${countryCode}). Raw series name: ${_found?.name}`);
          
          if (countryCode) {
            const countryStats = message?.raw?.regionStats?.find((r: any) => r.country === countryCode);
            const countryLabels = (countryStats as any)?.[period]?.labels || [];
            const sparseData = _found?.data || [];
            const aligned = alignSparseData(globalLabels, countryLabels, sparseData);
            console.log(`   Labels (global): ${JSON.stringify(globalLabels)}`);
            console.log(`   Labels (country): ${JSON.stringify(countryLabels)}`);
            console.log(`   Sparse data: ${JSON.stringify(sparseData)}`);
            console.log(`   Aligned data: ${JSON.stringify(aligned)}`);
            obj.data = aligned;
          } else {
            // Total Sales
            const sparseData = _found?.data || [];
            if (sparseData.length === globalLabels.length) {
              obj.data = sparseData.map((d: number) => Math.round(d));
            } else {
              obj.data = Array(globalLabels.length).fill(0);
              sparseData.forEach((d: number, i: number) => {
                if (i < obj.data.length) obj.data[i] = Math.round(d);
              });
            }
          }
        }
      }
    }
  }

  return true;
};
